import React, { useState, useEffect, useRef, useCallback  } from 'react';
import io from "socket.io-client";
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import classes from './MainContainer.module.scss';
import Header from '../../components/Header/Header';
import Alert from '../../components/Alert/Alert';
import FeedbackModal from '../../components/FeedbackModal/FeedbackModal';
import EndChatModal from "../../components/EndChatModal/EndChatModal";
import MessageComposer from '../../components/MessageComposer/MessageComposer';
import MessageList from '../MessageList/MessageList';
import Typing from '../Typing/Typing';
import QuickResponseContainer from '../QuickResponseContainer/QuickResponseContainer';
import Message from '../../models/Message';
import QuickResponse from '../../models/QuickResponse';
import { setSessionId, setAgentName, setResetChat, setEndChat, setFingerprintId, setAuthenticated, setEndChatIsOpen, setIsLoggedIn } from '../../store/actions/general';
import { addMessage, cancelFileUploads, setQuickResponses, setMode, setIsTyping, setMessages } from '../../store/actions/messages';
import { useDispatch, useSelector } from 'react-redux';

const flattenObject = (obj, parentKey = '', res = {}) => {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = parentKey ? `${parentKey}_${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        flattenObject(obj[key], newKey, res);
      } else {
        res[newKey] = obj[key];
      }
    }
  }
  return res;
};

const MainContainer = () => {
  const { prechatEnabled, widgetIsOpen, sessionId, resetChat, endChat, fingerprintId, authenticated, endChatIsOpen, isLoggedIn } = useSelector(state => state.general);
  const { messages, typing } = useSelector(state => state.messages);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const socketRef = useRef(null);
  const dispatch = useDispatch();

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    setModalOpen(endChat);
  },[endChat])

  const handleCloseEndchatModal = () => {
    dispatch(setEndChatIsOpen(false));
  };

  const handleSocketCleanup = useCallback(() => {
    if (socketRef.current) {
      try {
        socketRef.current.disconnect(); 
        socketRef.current.close(); 
      } catch (err) {
        console.error("Error during socket cleanup:", err);
      } finally {
        socketRef.current = null;  
        dispatch(setSessionId(null));
        dispatch(setResetChat(true));
      }
    } else {
      dispatch(setSessionId(null));
      dispatch(setResetChat(true));
    }
  }, [dispatch]);

  useEffect(() => {
    window.sutherland_variables.resetChat = resetChat;
    window.sutherland_variables.fingerprintId = fingerprintId
    if (!socketRef.current) {
      socketRef.current = io(window.sutherland_variables.engineBaseUrl, {
        path: '/ws/chat/',
        transports: ['websocket', 'polling'],
        upgrade: true,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelayMax: 2000,
        randomizationFactor: 0.5,
        auth: {
          bot_id: window.sutherland_variables.botId, // The bot config ID
          session_id: !sessionId ? '': sessionId
        }
      });

      const socket = socketRef.current;

      socket.on('connected', async (data) => {
        console.log('\u25B6 connected');
        if (window.sutherland_variables.resetChat || window.sutherland_variables.resetChat === null) {
          console.log('\u25B6 initialize session');
          dispatch(setSessionId(data.session_id))
          if (!window.sutherland_variables.fingerprintId) {
            const fp = await FingerprintJS.load();
            const { visitorId } = await fp.get();
            window.sutherland_variables.fingerprintId = visitorId
            dispatch(setFingerprintId(visitorId));
          }
          // Auto send message to start convo
          console.log('\u25B6 start chat');
          const hasTms = window.tmsData && window.tmsData.tms && window.tmsData.tms.loanNumber;
          const tmsFlattened = hasTms ? flattenObject(window.tmsData.tms) : {};

          const params = {
            authenticated: !!hasTms,
            ...tmsFlattened
          };

          dispatch(setAuthenticated(!!hasTms));

          socket.emit("send", {
            name: "welcome-event",
            parameters:
              {
                FingerprintId: window.sutherland_variables.fingerprintId, 
                UserAgent: window.sutherland_variables.user_agent,
                Browser: window.sutherland_variables.browser,
                DeviceCategory: window.sutherland_variables.device,
                ScreenResolution: `${window.screen.width}x${window.screen.height}`,
                VisitorIP: window.sutherland_variables.ip_address,
                url: document.URL,
                title: document.title,
                authenticated: authenticated,
                ...params,
                ...window.sutherland_variables.client.dynamic_variables,
                bot_id: window.sutherland_variables.botId
              }
          }); 
          dispatch(setResetChat(false));
        } else {
          const hasTms = window.tmsData && window.tmsData.tms && window.tmsData.tms.loanNumber;
          const tmsFlattened = hasTms ? flattenObject(window.tmsData.tms) : {};

          const params = {
            authenticated: !!hasTms,
            ...tmsFlattened,
            bot_id: window.sutherland_variables.botId
          };

          if (params.authenticated) {
            if(!isLoggedIn) {
              dispatch(setIsLoggedIn(true));
              const message = {
                "type": "status",
                "text": "----- You are now logged in -----",
                "mode": "status"
              }
              dispatch(addMessage(new Message(message)));
              socket.emit("send", {
                name: "logged-in-event",
                parameters: params
              });
            }
          } else {
            if(isLoggedIn) {
              dispatch(setIsLoggedIn(false));
              socket.emit("send", {
                name: "logged-off-event",
                parameters: {"bot_id": window.sutherland_variables.botId}
              });
              dispatch(setMessages([]))
            }
          }

          dispatch(setAuthenticated(!!hasTms));
        }
      });

      socket.on('disconnected', (data) => {
        console.log('Client disconnected.')
      });

      socket.on('message-received', (data) => {
        data.mode = data.mode.toLowerCase()
        dispatch(setMode(data.mode))
        dispatch(setIsTyping(false))
        for (const message of data.payload) {
          message.mode = data.mode
          switch(message.type) {
            case 'chips':
              const responsesList = [];
              for (const option of message.options) {
                const newQuickResponse = new QuickResponse(option);
                responsesList.push(newQuickResponse);
              }
              dispatch(setQuickResponses(responsesList));
              break;
            case 'info':
              if (data.mode === "agent") {
                dispatch(setAgentName(data.username));
              }
              message.text = `[${message.title}](${message.link})`;
              dispatch(addMessage(new Message(message)));
              break;
            case 'status':
              dispatch(addMessage(new Message(message)));
              switch (message.event) {
                case "ChatEnd":
                  dispatch(setEndChat(true));
                  socket.disconnect();
                  break;
                case "FileRequestCancel":
                  dispatch(cancelFileUploads())
                  break;
                default:
                  break;
              }
              break;
            case 'feedback':
              dispatch(setEndChat(true));
              break;
            case 'carousel':
              const carouselResponse = {
                  ...message,
                "parameters": message.options
              }
              dispatch(addMessage(new Message(carouselResponse)));
            break;
            default:
              if (message.type !== 'error' && message.type !== 'liveAgentHandoff') {
                if (data.mode === "agent") {
                  dispatch(setAgentName(data.username));
                }
                dispatch(addMessage(new Message(message)));
              }
              break;
          }
        }
      });

      socket.on('agent-typing', () => {
        dispatch(setMode("agent"))
        dispatch(setIsTyping(true))
        setTimeout(() => {
          dispatch(setIsTyping(false))
        }, 1500)
      });

      socket.on('stop-typing', () => {
        dispatch(setMode("agent"))
        dispatch(setIsTyping(false))
      });

      socket.on('file-transfer', (data) => {
        dispatch(setIsTyping(false))
        const message = {
          "type": "file-transfer",
          "text": "",
          "url": data?.url,
        }
        dispatch(addMessage(new Message(message)));
      });
      setIsLoading(false)
    }
  }, [dispatch, sessionId, messages, resetChat, fingerprintId, authenticated, handleSocketCleanup, isLoggedIn]);

  if (!isLoading) {
    return (
        <div
        className={`${prechatEnabled || widgetIsOpen ? classes.MainContainer : classes.MainContainerGrowIn} ${
          widgetIsOpen ? classes['widget-open'] : classes['widget-closed']
        }`}>
        <div className={classes.MainContainerOuter}> 
          <Header socket={socketRef.current}/>
          <Alert socket={socketRef.current}/>
          <FeedbackModal isOpen={modalOpen} onClose={handleCloseModal} socket={socketRef.current} />
          <EndChatModal
            isOpen={endChatIsOpen}
            closeHandler={handleCloseEndchatModal}
            socket={socketRef.current}
          />
          <MessageList  socket={socketRef.current} />
            {!endChat && <Typing show={typing} mode="dot-loader" typingText="" /> }
            <QuickResponseContainer socket={socketRef.current} handleSocketCleanup={handleSocketCleanup} />
            <div className={classes.footer}>
              <MessageComposer socket={socketRef.current}/>
            </div>
        </div>
      </div>
    );
  }
  return <div></div>
};

export default MainContainer;
